import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { 
  Sparkles, 
  FileText, 
  BookOpen, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  Trash2,
  RefreshCw,
  ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MockQuestionGenerator = ({ schoolId, schoolName, token, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [clearing, setClearing] = useState(false);
  
  // Materials and chapters state
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [chapters, setChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [selectedChapters, setSelectedChapters] = useState([]);  // Changed to array for multiple selection
  
  // Generation options
  const [numQuestions, setNumQuestions] = useState(10);
  const [replaceExisting, setReplaceExisting] = useState(false);
  
  // Current mock exam info
  const [mockExamInfo, setMockExamInfo] = useState(null);
  const [generationResult, setGenerationResult] = useState(null);

  // Fetch materials when dialog opens
  const fetchMaterials = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/mock-exam/materials`);
      if (response.ok) {
        const data = await response.json();
        setMaterials(data.materials || []);
        setMockExamInfo({
          currentQuestions: data.current_mock_questions,
          hasCustom: data.has_custom_questions
        });
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    if (open) {
      fetchMaterials();
      setGenerationResult(null);
    }
  }, [open, fetchMaterials]);

  // Also fetch on initial mount to show correct status in card
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // Fetch chapters when material is selected
  const fetchChapters = useCallback(async (materialId) => {
    if (!materialId || !schoolId) return;
    setLoadingChapters(true);
    setChapters([]);
    setSelectedChapters([]);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/mock-exam/chapters/${materialId}`);
      if (response.ok) {
        const data = await response.json();
        setChapters(data.chapters || []);
        // Auto-select first chapter if available
        if (data.chapters && data.chapters.length > 0) {
          setSelectedChapters([data.chapters[0].id]);
        }
      }
    } catch (error) {
      console.error('Error fetching chapters:', error);
      toast.error('Failed to extract chapters from PDF');
    } finally {
      setLoadingChapters(false);
    }
  }, [schoolId]);

  useEffect(() => {
    if (selectedMaterial) {
      fetchChapters(selectedMaterial);
    }
  }, [selectedMaterial, fetchChapters]);

  const handleGenerate = async () => {
    if (!selectedMaterial) {
      toast.error('Please select a training material');
      return;
    }

    if (!schoolId) {
      toast.error('School ID is missing. Please refresh the page.');
      console.error('MockQuestionGenerator: schoolId is undefined or empty');
      return;
    }

    if (!token) {
      toast.error('Please log in to generate questions');
      return;
    }

    setGenerating(true);
    setGenerationResult(null);

    try {
      console.log('Generating mock questions for school:', schoolId);
      console.log('Material ID:', selectedMaterial);
      console.log('Selected chapters:', selectedChapters);
      
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/mock-exam/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          material_id: selectedMaterial,
          chapter_ids: selectedChapters,
          num_questions: numQuestions,
          replace_existing: replaceExisting
        })
      });

      const data = await response.json();
      console.log('Generation response:', data);

      if (response.ok) {
        setGenerationResult(data);
        setMockExamInfo(prev => ({
          ...prev,
          currentQuestions: data.total_mock_questions,
          hasCustom: true
        }));
        toast.success(data.message);
      } else {
        console.error('Generation failed:', data);
        toast.error(data.detail || data.message || 'Failed to generate questions');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate questions: ' + (error.message || 'Network error'));
    } finally {
      setGenerating(false);
    }
  };

  // Toggle chapter selection
  const toggleChapterSelection = (chapterId) => {
    setSelectedChapters(prev => {
      if (prev.includes(chapterId)) {
        return prev.filter(id => id !== chapterId);
      } else {
        return [...prev, chapterId];
      }
    });
  };

  // Select all chapters
  const selectAllChapters = () => {
    setSelectedChapters(chapters.map(ch => ch.id));
  };

  // Deselect all chapters
  const deselectAllChapters = () => {
    setSelectedChapters([]);
  };

  const handleClearQuestions = async () => {
    if (!window.confirm('Are you sure you want to clear custom mock questions? This will revert to default questions.')) {
      return;
    }

    setClearing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/mock-exam/questions/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setMockExamInfo(prev => ({
          ...prev,
          currentQuestions: data.default_question_count,
          hasCustom: false
        }));
        setGenerationResult(null);
      } else {
        toast.error(data.detail || 'Failed to clear questions');
      }
    } catch (error) {
      console.error('Clear error:', error);
      toast.error('Failed to clear questions');
    } finally {
      setClearing(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-emerald-800">
              <Sparkles className="text-emerald-600" />
              Laikos Mock Exams Question Generator
            </CardTitle>
            <CardDescription className="text-emerald-600">
              Generate mock exam questions from training material chapters
            </CardDescription>
          </div>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="open-mock-generator-btn"
              >
                <Sparkles size={16} className="mr-2" />
                Generate Mock Questions
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="text-emerald-600" />
                  Laikos Mock Exams Question Generator
                </DialogTitle>
                <DialogDescription>
                  Select a training material and chapter to generate mock exam questions
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Step 1: Select Material */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                    Select Training Material
                  </Label>
                  
                  {loading ? (
                    <div className="flex items-center gap-2 text-gray-500 p-4">
                      <Loader2 className="animate-spin" size={16} />
                      Loading materials...
                    </div>
                  ) : materials.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle size={18} />
                        <span>No PDF materials available. Please upload training materials first.</span>
                      </div>
                    </div>
                  ) : (
                    <Select value={selectedMaterial} onValueChange={setSelectedMaterial}>
                      <SelectTrigger data-testid="material-select">
                        <SelectValue placeholder="Choose a PDF material" />
                      </SelectTrigger>
                      <SelectContent>
                        {materials.map(material => (
                          <SelectItem key={material.id} value={material.id}>
                            <div className="flex items-center gap-2">
                              <FileText size={14} className="text-emerald-600" />
                              {material.title}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Step 2: Select Chapter */}
                {selectedMaterial && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                      Select Chapters/Sections
                    </Label>
                    
                    {loadingChapters ? (
                      <div className="flex items-center gap-2 text-gray-500 p-4 bg-gray-50 rounded-lg">
                        <Loader2 className="animate-spin" size={16} />
                        Analyzing PDF structure...
                      </div>
                    ) : chapters.length === 0 ? (
                      <div className="bg-gray-50 border rounded-lg p-4 text-gray-500">
                        No chapters detected. Will generate from full document.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Select/Deselect All buttons */}
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm text-gray-600">
                            {selectedChapters.length === 0 
                              ? 'No chapters selected (will use full document)' 
                              : `${selectedChapters.length} chapter(s) selected`}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={selectAllChapters}
                              className="text-xs"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={deselectAllChapters}
                              className="text-xs"
                            >
                              Clear
                            </Button>
                          </div>
                        </div>
                        
                        {/* Chapter list with checkboxes */}
                        <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2">
                          {chapters.map(chapter => (
                            <div
                              key={chapter.id}
                              onClick={() => toggleChapterSelection(chapter.id)}
                              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                selectedChapters.includes(chapter.id)
                                  ? 'bg-emerald-50 border-emerald-300'
                                  : 'bg-white hover:bg-gray-50'
                              }`}
                              data-testid={`chapter-${chapter.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox 
                                  checked={selectedChapters.includes(chapter.id)}
                                  className="data-[state=checked]:bg-emerald-600"
                                  onClick={(e) => e.stopPropagation()}
                                  onCheckedChange={() => toggleChapterSelection(chapter.id)}
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-800">{chapter.title}</p>
                                  {chapter.description && (
                                    <p className="text-sm text-gray-500 mt-1">{chapter.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          Select multiple chapters to generate questions from different sections. Questions will be distributed evenly across selected chapters.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Configure Options */}
                {selectedMaterial && (
                  <div className="space-y-4">
                    <Label className="flex items-center gap-2">
                      <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                      Configure Generation
                    </Label>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="numQuestions" className="text-sm">Number of Questions</Label>
                        <Input
                          id="numQuestions"
                          type="number"
                          min={5}
                          max={30}
                          value={numQuestions}
                          onChange={(e) => setNumQuestions(parseInt(e.target.value) || 10)}
                          data-testid="num-questions-input"
                        />
                      </div>
                      <div className="flex items-end">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="replaceExisting"
                            checked={replaceExisting}
                            onCheckedChange={setReplaceExisting}
                          />
                          <Label htmlFor="replaceExisting" className="text-sm cursor-pointer">
                            Replace existing questions
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generation Result */}
                {generationResult && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                      <CheckCircle size={18} />
                      Mock Questions Generated!
                    </div>
                    <div className="text-sm text-emerald-600 space-y-1">
                      <p>• Generated: {generationResult.questions_generated} questions</p>
                      <p>• Total mock questions: {generationResult.total_mock_questions}</p>
                      <p>• Source: {generationResult.source_chapter}</p>
                      {generationResult.chapters_used && generationResult.chapters_used.length > 1 && (
                        <p>• Chapters used: {generationResult.chapters_used.length}</p>
                      )}
                    </div>
                    {generationResult.questions_preview && generationResult.questions_preview.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <p className="text-xs font-medium text-emerald-700 mb-2">Sample Questions:</p>
                        <div className="space-y-2">
                          {generationResult.questions_preview.map((q, idx) => (
                            <div key={idx} className="text-xs bg-white/50 rounded p-2">
                              <p className="font-medium">{idx + 1}. {q.question_text}</p>
                              {q.source_chapter && (
                                <p className="text-emerald-500 mt-1">From: {q.source_chapter}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between items-center pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleClearQuestions}
                    disabled={clearing || !mockExamInfo?.hasCustom}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {clearing ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : (
                      <Trash2 size={16} className="mr-2" />
                    )}
                    Clear Custom Questions
                  </Button>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setOpen(false)}>
                      Close
                    </Button>
                    <Button
                      onClick={handleGenerate}
                      disabled={generating || !selectedMaterial || !token}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="generate-mock-btn"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} className="mr-2" />
                          Generate {numQuestions} Questions
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* PDF Materials Count */}
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <FileText size={18} />
              <span className="font-medium">PDF Materials</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{materials.length || '—'}</p>
            <p className="text-xs text-gray-500">Available for generation</p>
          </div>

          {/* Current Questions */}
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-2 text-teal-600 mb-1">
              <BookOpen size={18} />
              <span className="font-medium">Mock Questions</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{mockExamInfo?.currentQuestions || '—'}</p>
            <p className="text-xs text-gray-500">
              {mockExamInfo?.hasCustom ? 'Custom generated' : 'Default questions'}
            </p>
          </div>

          {/* Status */}
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <CheckCircle size={18} />
              <span className="font-medium">Status</span>
            </div>
            <Badge className={mockExamInfo?.hasCustom ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
              {mockExamInfo?.hasCustom ? 'Custom Questions Active' : 'Using Default'}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MockQuestionGenerator;
